import React, { createContext, useContext, useState, useEffect } from 'react';

interface ThemeContextType {
  isDark: boolean;
  toggleTheme: () => void;
  theme: typeof darkTheme;
}

// ========================================================
// NEXO CRM — PALETA CORPORATIVA DE TEMA
// ========================================================

export const darkTheme = {
  bg: '#071516',
  bgSecondary: '#0c2123',
  card: '#0a1c1e',
  cardHover: '#0f2b2d',
  border: 'rgba(255, 255, 255, 0.08)',
  borderLight: 'rgba(255, 255, 255, 0.05)',
  text: '#f8fafc',
  textSecondary: '#94a3b8',
  textMuted: '#64748b',
  accent: '#00df82',
  accentHover: '#00f58f',
  accentLight: 'rgba(0, 223, 130, 0.18)',
  success: '#10b981',
  successBg: 'rgba(16, 185, 129, 0.15)',
  warning: '#f59e0b',
  warningBg: 'rgba(245, 158, 11, 0.15)',
  danger: '#ef4444',
  dangerBg: 'rgba(239, 68, 68, 0.15)',
  purple: '#00df82',
  purpleBg: 'rgba(0, 223, 130, 0.18)',
  sidebarBg: '#071516',
  sidebarActive: 'rgba(0, 223, 130, 0.22)',
  sidebarText: '#f8fafc',
  inputBg: '#0c2123',
  shadow: '0 4px 24px rgba(0, 0, 0, 0.6)',
  shadowSm: '0 2px 10px rgba(0, 0, 0, 0.4)',
};

export const lightTheme = {
  bg: '#f8fafc',
  bgSecondary: '#f1f5f9',
  card: '#ffffff',
  cardHover: '#f8fafc',
  border: '#e2e8f0',
  borderLight: '#f1f5f9',
  text: '#0f172a',
  textSecondary: '#475569',
  textMuted: '#94a3b8',
  accent: '#063c3d',
  accentHover: '#042425',
  accentLight: 'rgba(0, 223, 130, 0.12)',
  success: '#10b981',
  successBg: '#d1fae5',
  warning: '#d97706',
  warningBg: '#fef3c7',
  danger: '#ef4444',
  dangerBg: '#fee2e2',
  purple: '#063c3d',
  purpleBg: 'rgba(6, 60, 61, 0.1)',
  sidebarBg: '#ffffff',
  sidebarActive: 'rgba(6, 60, 61, 0.08)',
  sidebarText: '#0f172a',
  inputBg: '#ffffff',
  shadow: '0 4px 20px rgba(15, 23, 42, 0.06)',
  shadowSm: '0 2px 8px rgba(15, 23, 42, 0.04)',
};

const ThemeContext = createContext<ThemeContextType>({
  isDark: false,
  toggleTheme: () => {},
  theme: lightTheme,
});

export const ThemeProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const isDark = false;
  const toggleTheme = () => {};
  const theme = lightTheme;

  useEffect(() => {
    localStorage.setItem('nexo_theme', 'light');
    document.body.style.background = '#f8fafc';
    document.body.style.color = '#0f172a';
    document.body.style.fontFamily = "'Plus Jakarta Sans', -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif";
    document.body.classList.add('light');

    const mv: Record<string, [string, string]> = {
      'z-bg': ['#071516', '#f8fafc'],
      'z-card': ['#0a1c1e', '#ffffff'],
      'z-surface': ['#0c2123', '#f1f5f9'],
      'z-surface2': ['#0f2b2d', '#e2e8f0'],
      'z-border': ['rgba(255,255,255,0.08)', '#e2e8f0'],
      'z-border2': ['rgba(255,255,255,0.12)', '#cbd5e1'],
      'z-border3': ['rgba(255,255,255,0.18)', '#94a3b8'],
      'z-text': ['#f8fafc', '#0f172a'],
      'z-text-sub': ['rgba(248,250,252,0.7)', '#475569'],
      'z-text-muted': ['rgba(248,250,252,0.45)', '#94a3b8'],
      'z-modal-bg': ['#0a1c1e', '#ffffff'],
      'z-input-bg': ['#0c2123', '#ffffff'],
      'z-hover': ['rgba(255,255,255,0.04)', '#f1f5f9'],
      'z-wa-sidebar': ['#0c2123', '#ffffff'],
      'z-wa-chat': ['#071516', '#f8fafc'],
      'z-wa-msg-in': ['#0f2b2d', '#ffffff'],
      'z-wa-msg-out': ['#063c3d', '#e6f7f2'],
      'z-wa-text': ['#f8fafc', '#0f172a'],
      'z-wa-text-sub': ['#94a3b8', '#64748b'],
      'z-menu-bg': ['#0a1c1e', '#ffffff'],
      'z-panel-bg': ['#0c2123', '#f8fafc'],
      'z-modal2-bg': ['#0c2123', '#ffffff'],
      'z-select-bg': ['#0f2b2d', '#ffffff'],
      'z-re-bg': ['#071516', '#f8fafc'],
      'z-re-card': ['#0a1c1e', '#ffffff'],
      'z-re-card-grad': ['linear-gradient(145deg,#0c2123,#0f2b2d)', 'linear-gradient(145deg,#ffffff,#f8fafc)'],
      'z-re-overlay': ['rgba(7,21,22,0.95)', 'rgba(255,255,255,0.96)'],
      'z-white': ['#f8fafc', '#0f172a'],
      'z-wm-bg': ['#0c2123', '#ffffff'],
      'z-wm-bg2': ['#071516', '#f8fafc'],
      'z-wm-bg3': ['#042425', '#f1f5f9'],
      'z-wm-border': ['rgba(255,255,255,0.08)', '#e2e8f0'],
      'z-wm-text': ['#f8fafc', '#0f172a'],
      'z-wm-text-sub': ['#94a3b8', '#475569'],
      'z-wm-text-muted': ['#64748b', '#94a3b8'],
      'z-wm-text-faint': ['#475569', '#cbd5e1'],
      'z-wm-header': ['linear-gradient(135deg, #063c3d 0%, #042425 100%)', '#ffffff'],
      'z-wm-header-text': ['#ffffff', '#0f172a'],
      'z-wm-header-sub': ['rgba(255,255,255,0.7)', '#64748b'],
      'z-wm-header-btn': ['rgba(255,255,255,0.15)', 'rgba(6,60,61,0.08)'],
      'z-wm-header-btn-text': ['#ffffff', '#063c3d'],
      'z-tab-active-bg': ['rgba(0,223,130,0.18)', 'rgba(6,60,61,0.08)'],
      'z-tab-active-text': ['#00df82', '#063c3d'],
      'z-tab-text': ['rgba(248,250,252,0.6)', '#64748b'],
      'z-btn-primary': ['linear-gradient(135deg, #063c3d, #042425)', 'linear-gradient(135deg, #063c3d, #03624c)'],
      'z-chart-grid': ['rgba(255,255,255,0.08)', '#e2e8f0'],
      'z-chart-tooltip-bg': ['rgba(7,21,22,0.96)', 'rgba(255,255,255,0.98)'],
      'z-chart-tooltip-text': ['#f8fafc', '#0f172a'],
      'z-chart-axis': ['rgba(248,250,252,0.4)', '#94a3b8'],
      'z-chart-dot-stroke': ['#0a1c1e', '#ffffff'],
      'z-chart-empty': ['rgba(248,250,252,0.2)', '#cbd5e1'],
      'z-accent-soft': ['#00df82', '#063c3d'],
      'z-accent-soft2': ['#00f58f', '#03624c'],
      'z-ind-border': ['rgba(0,223,130,0.3)', 'rgba(6,60,61,0.2)'],
      'z-ind-border-soft': ['rgba(0,223,130,0.2)', 'rgba(6,60,61,0.12)'],
      'z-ind-border-faint': ['rgba(0,223,130,0.1)', 'rgba(6,60,61,0.06)'],
      'z-ind-bg': ['rgba(0,223,130,0.14)', 'rgba(6,60,61,0.06)'],
      'z-ind-bg-soft': ['rgba(0,223,130,0.08)', 'rgba(6,60,61,0.04)'],
      'z-ind-bg-faint': ['rgba(0,223,130,0.04)', 'rgba(6,60,61,0.02)'],
      'z-ind-bg-strong': ['rgba(0,223,130,0.22)', 'rgba(6,60,61,0.12)'],
      'z-text-strong': ['#f8fafc', '#0f172a'],
      'z-text-soft': ['rgba(248,250,252,0.6)', '#64748b'],
    };

    Object.entries(mv).forEach(([k, v]) => document.documentElement.style.setProperty('--' + k, isDark ? v[0] : v[1]));
  }, [isDark, theme]);

  return (
    <ThemeContext.Provider value={{ isDark, toggleTheme, theme }}>
      {children}
    </ThemeContext.Provider>
  );
};

export const useTheme = () => useContext(ThemeContext);
